import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import { StatCard, SectionTitle } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import {
  Users, FileText, ClipboardCheck, Database, UserCheck, Sparkles,
  Plus, ArrowRight, Activity, CheckCircle2,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, PieChart, Pie, Tooltip,
} from "recharts";

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/dashboard");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    await api.post(`/admin/students/${id}/approve`);
    load();
  };

  const s = data?.stats || {};
  const statusData = Object.entries(data?.status_counts || {}).map(([k, v]) => ({
    name: k.replace(/_/g, " "), value: v,
  }));
  const COLORS = ["#FFB020", "#059669", "#E0000F", "#71717a", "#002FA7"];

  return (
    <DashboardLayout nav={adminNav} title="Overview">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Admin Control Room</h1>
          <p className="text-muted-foreground mt-1">Monitor enrollment, examinations and platform activity.</p>
        </div>
        <Link to="/admin/exams">
          <Button className="rounded-sm" data-testid="quick-create-exam"><Plus className="h-4 w-4 mr-1.5" /> Create Examination</Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mb-8">
        <StatCard label="Total Students" value={s.total_students ?? "—"} icon={Users} index={0} sub={`${s.approved_students ?? 0} approved`} />
        <StatCard label="Pending Approvals" value={s.pending_approvals ?? "—"} icon={UserCheck} accent="text-amber-500" index={1} />
        <StatCard label="Examinations" value={s.total_exams ?? "—"} icon={FileText} index={2} sub={`${s.published_exams ?? 0} published`} />
        <StatCard label="Pending Evaluations" value={s.pending_evaluations ?? "—"} icon={ClipboardCheck} accent="text-destructive" index={3} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 bg-card border border-border p-6">
          <SectionTitle>Enrollment by Status</SectionTitle>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={statusData}>
              <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "#f3f4f6" }} />
              <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card border border-border p-6">
          <SectionTitle>Question Sources</SectionTitle>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={[
                  { name: "AI", value: s.ai_questions || 0 },
                  { name: "Manual", value: Math.max((s.bank_questions || 0) - (s.ai_questions || 0), 0) },
                ]}
                dataKey="value" innerRadius={45} outerRadius={75} paddingAngle={2}
              >
                <Cell fill="#002FA7" />
                <Cell fill="#cbd5e1" />
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 text-xs">
            <span className="flex items-center gap-1"><span className="h-2 w-2 bg-primary inline-block" /> AI ({s.ai_questions || 0})</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 bg-slate-300 inline-block" /> Manual</span>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border p-6">
          <SectionTitle action={<Link to="/admin/students" className="text-sm text-primary flex items-center gap-1">View all <ArrowRight className="h-3.5 w-3.5" /></Link>}>
            Pending Approvals
          </SectionTitle>
          <div className="space-y-2">
            {(data?.pending_students || []).length === 0 && (
              <div className="text-sm text-muted-foreground py-6 text-center">No pending requests 🎉</div>
            )}
            {(data?.pending_students || []).map((st) => (
              <div key={st.id} className="flex items-center gap-3 border border-border p-3" data-testid={`pending-row-${st.id}`}>
                <div className="h-9 w-9 bg-secondary flex items-center justify-center text-sm font-semibold">{st.name?.[0]}</div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{st.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{st.registration_number}</div>
                </div>
                <Button size="sm" className="ml-auto rounded-sm" onClick={() => approve(st.id)} data-testid={`approve-${st.id}`}>
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border p-6">
          <SectionTitle>Recent Activity</SectionTitle>
          <div className="space-y-3">
            {(data?.recent_activities || []).length === 0 && (
              <div className="text-sm text-muted-foreground py-6 text-center">No activity yet</div>
            )}
            {(data?.recent_activities || []).map((a) => (
              <div key={a.id} className="flex items-start gap-3">
                <Activity className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm">{a.text}</div>
                  <div className="text-xs text-muted-foreground font-mono">{new Date(a.created_at).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
